import { HttpErrorResponse } from '@angular/common/http';

import { ApiErrorResponse } from '../models/api-error-response';

/**
 * Default message shown when no API error message is available.
 */
const DEFAULT_ERROR_MESSAGE = 'An error occurred.';

/**
 * Extracts the user-readable error message from an HTTP error response.
 * If the API returned an {@link ApiErrorResponse} body (with message, reason, statusCode), its message is used;
 * otherwise the error's message or a default is returned.
 *
 * @param error - The error from HttpClient subscribe (typically HttpErrorResponse).
 * @returns User-readable error message to display.
 */
export function getApiErrorMessage(error: unknown): string {
  if (error instanceof HttpErrorResponse && error.error && typeof error.error === 'object') {
    const body = error.error as Partial<ApiErrorResponse>;
    if (typeof body.message === 'string' && body.message.trim() !== '') {
      return body.message;
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return DEFAULT_ERROR_MESSAGE;
}

/**
 * Extracts the reason/category from an HTTP error response when the API returned an {@link ApiErrorResponse}.
 * Can be used to show a label like "Reason: Permissions" or for styling.
 *
 * @param error - The error from HttpClient subscribe (typically HttpErrorResponse).
 * @returns The reason string (e.g. "Permissions", "NotFound") or undefined if not present.
 */
export function getApiErrorReason(error: unknown): string | undefined {
  if (error instanceof HttpErrorResponse && error.error && typeof error.error === 'object') {
    const body = error.error as Partial<ApiErrorResponse>;
    if (typeof body.reason === 'string' && body.reason.trim() !== '') {
      return body.reason;
    }
  }
  return undefined;
}

/**
 * Builds a single display string from the API error (message and optional reason).
 * Use this when you want to show one line in a snackbar, e.g. "You do not have permission. (Permissions)"
 *
 * @param error - The error from HttpClient subscribe (typically HttpErrorResponse).
 * @returns Message, or "Message (Reason)" if reason is present.
 */
export function getApiErrorDisplayText(error: unknown): string {
  const message = getApiErrorMessage(error);
  const reason = getApiErrorReason(error);
  if (reason) {
    return `${message} (${reason})`;
  }
  return message;
}
